/** Deterministic, immutable card duel. Decisions, not wall time, advance the match. */
import { Rng } from '../../shared';
import { CARD_POOL, STARTER_DECK, type CardDef } from '../../content/cards/catalog';
import { opponentDeck, type Opponent } from './opponents';

export type Lane = 0 | 1;
export interface Unit { id: string; lane: Lane; bonus: number }
export interface Duel {
  id: string; seed: string; round: number; turn: 0 | 1; wins: [number, number];
  hands: [string[], string[]]; decks: [string[], string[]]; board: [Unit[], Unit[]];
  passed: [boolean, boolean]; leader: [boolean, boolean];
  status: 'playing' | 'round' | 'finished'; outcome: 'win' | 'loss' | 'draw' | null;
  roundScores: [number, number][]; last: string; rewardClaimed: boolean;
  prizeCard?: string; rewardLines?: Array<{ key: string; params: Record<string, string | number> }>;
  opponent?: Opponent;
  /** Absent in saved legacy matches. Their rules never change mid-match. */
  aiVersion?: 2;
  ruleset?: 2; orders?: [number, number]; standards?: [Lane, Lane, Lane];
  beat?: { side: 0 | 1; kind: Move['kind']; card?: string; lane?: Lane; before: [number, number]; after: [number, number]; serial: number;
    changes?: Array<{ side: 0 | 1; id: string; before: number; after: number }>;
    standard?: { before: 0 | 1 | null; after: 0 | 1 | null } };
}
export type Move = { kind: 'play'; index: number; lane: Lane } | { kind: 'pass' } | { kind: 'leader'; lane: Lane } | { kind: 'recall' | 'shift'; index: number };
const CARD_BY_ID = new Map(CARD_POOL.map(c => [c.id, c]));
export function cardById(id: string): CardDef { return CARD_BY_ID.get(id) ?? CARD_POOL[0]; }
export function validDeck(ids: readonly string[], owned: readonly string[]): boolean {
  return ids.length === 12 && new Set(ids).size === 12 && ids.every(id => owned.includes(id) && CARD_POOL.some(c => c.id === id));
}
/** Atomic replacement keeps a playable twelve-card deck throughout the choice. */
export function replaceInDeck(ids: readonly string[], owned: readonly string[], remove: string, add: string): string[] {
  if (!ids.includes(remove) || ids.includes(add) || !owned.includes(add)) return [...ids];
  const next = ids.map(id => id === remove ? add : id);
  return validDeck(next, owned) ? next : [...ids];
}
export function deckProfile(ids: readonly string[]): { families: Record<CardDef['family'], number>; rows: [number, number]; bonds: number } {
  const families = { cat: 0, hare: 0, snail: 0, goose: 0 }, rows: [number, number] = [0, 0]; let bonds = 0;
  for (const id of ids) { const c = cardById(id); families[c.family]++; if (c.lane !== 1) rows[0]++; if (c.lane !== 0) rows[1]++; if (c.effect === 'bond') bonds++; }
  return { families, rows, bonds };
}
export function newDuel(seed: string, index: number, deck: readonly string[] = STARTER_DECK): Duel {
  const key = `${seed}:cards:${index}`;
  const rng = new Rng(key);
  const a = rng.stream('player').shuffle([...deck]);
  const rival = opponentDeck(seed, index);
  const b = rng.stream('opponent').shuffle(rival.deck);
  return { id: key, seed: key, round: 1, turn: 0, wins: [0, 0], hands: [a.splice(0, 8), b.splice(0, 8)], decks: [a, b],
    board: [[], []], passed: [false, false], leader: [false, false], status: 'playing', outcome: null,
    roundScores: [], last: 'start', rewardClaimed: false, opponent: rival.opponent,
    aiVersion: 2, ruleset: 2, orders: [2, 2], standards: rng.stream('standards').shuffle([0, 1, index % 2] as [Lane, Lane, Lane]) as [Lane, Lane, Lane] };
}
export function unitBreakdown(unit: Unit, board: readonly Unit[]): { base: number; kin: number; banners: number; effects: number; total: number } {
  const c = cardById(unit.id);
  const allies = board.filter(u => u !== unit && u.lane === unit.lane);
  const bond = c.effect === 'bond' ? allies.filter(u => cardById(u.id).family === c.family).length * 2 : 0;
  const banner = allies.filter(u => cardById(u.id).effect === 'banner').length;
  return { base: c.power, kin: bond, banners: banner, effects: unit.bonus, total: Math.max(0, c.power + unit.bonus + bond + banner) };
}
export function unitPower(unit: Unit, board: readonly Unit[]): number { return unitBreakdown(unit, board).total; }
export function laneScore(state: Duel, side: 0 | 1, lane: Lane): number {
  const units = state.board[side].filter(u => u.lane === lane);
  const kin = { cat: 0, hare: 0, snail: 0, goose: 0 }; let banners = 0;
  for (const unit of units) { const c = cardById(unit.id); kin[c.family]++; if (c.effect === 'banner') banners++; }
  return units.reduce((n, u) => { const c = cardById(u.id); return n + Math.max(0, c.power + u.bonus + (c.effect === 'bond' ? (kin[c.family] - 1) * 2 : 0) + banners - (c.effect === 'banner' ? 1 : 0)); }, 0);
}
export function standardLane(state: Duel): Lane | undefined { return state.ruleset === 2 ? state.standards?.[state.round - 1] : undefined; }
export function standardBonus(state: Duel, side: 0 | 1): number {
  const lane = standardLane(state);
  return lane !== undefined && laneScore(state, side, lane) > laneScore(state, side === 0 ? 1 : 0, lane) ? 4 : 0;
}
export function score(state: Duel, side: 0 | 1): number { return laneScore(state, side, 0) + laneScore(state, side, 1) + standardBonus(state, side); }
export function legalMoves(state: Duel): Move[] {
  if (state.status !== 'playing' || state.passed[state.turn]) return [];
  const side = state.turn;
  const moves: Move[] = [{ kind: 'pass' }];
  if (!state.leader[side] && (state.ruleset !== 2 || (state.orders?.[side] ?? 0) >= 2)) for (const lane of [0, 1] as const) if (state.board[side].some(u => u.lane === lane)) moves.push({ kind: 'leader', lane });
  if (state.ruleset === 2 && (state.orders?.[side] ?? 0) >= 1) state.board[side].forEach((_, index) => { moves.push({ kind: 'recall', index }, { kind: 'shift', index }); });
  state.hands[side].forEach((id, index) => {
    const card = cardById(id);
    for (const lane of [0, 1] as const) if (card.lane === 'any' || card.lane === lane) moves.push({ kind: 'play', index, lane });
  });
  return moves;
}
function finishRound(s: Duel): void {
  const a = score(s, 0), b = score(s, 1);
  s.roundScores.push([a, b]);
  if (a >= b) s.wins[0]++;
  if (b >= a) s.wins[1]++;
  s.status = s.wins[0] >= 2 || s.wins[1] >= 2 || s.round >= 3 ? 'finished' : 'round';
  s.last = a === b ? 'roundDraw' : a > b ? 'roundWin' : 'roundLoss';
  if (s.status === 'finished') s.outcome = s.wins[0] === s.wins[1] ? 'draw' : s.wins[0] > s.wins[1] ? 'win' : 'loss';
}
export function applyMove(state: Duel, move: Move): Duel {
  if (!legalMoves(state).some(m => JSON.stringify(m) === JSON.stringify(move))) return state;
  return transition(state, move, true);
}
/** The exact same effects/resolution power real and hypothetical moves. Only
 * persisted moves need a deep clone of historical receipts and a visual beat. */
function transition(state: Duel, move: Move, record: boolean): Duel {
  const s: Duel = record ? structuredClone(state) : { ...state,
    hands: [[...state.hands[0]], [...state.hands[1]]], decks: [[...state.decks[0]], [...state.decks[1]]],
    board: state.board.map(row => row.map(u => ({ ...u }))) as Duel['board'],
    passed: [...state.passed], leader: [...state.leader], wins: [...state.wins],
    ...(state.orders ? { orders: [...state.orders] as [number, number] } : {}), roundScores: [...state.roundScores] };
  const side = s.turn, enemy = side === 0 ? 1 : 0;
  const before: [number, number] = record ? [score(s, 0), score(s, 1)] : [0, 0];
  let played: string | undefined;
  if (move.kind === 'pass') { s.passed[side] = true; s.last = side === 0 ? 'youPassed' : 'opponentPassed'; }
  if (move.kind === 'leader') {
    s.leader[side] = true;
    if (s.ruleset === 2 && s.orders) s.orders[side] -= 2;
    for (const u of s.board[side]) if (u.lane === move.lane) u.bonus += 2;
    s.last = 'leader';
  }
  if (move.kind === 'play') {
    const [id] = s.hands[side].splice(move.index, 1);
    played = id;
    const c = cardById(id);
    s.board[side].push({ id, lane: move.lane, bonus: 0 });
    if (c.effect === 'raid') {
      const target = s.board[enemy].filter(u => u.lane === move.lane).sort((a, b) => unitPower(b, s.board[enemy]) - unitPower(a, s.board[enemy]))[0];
      if (target) target.bonus -= 3;
    }
    if (c.effect === 'scribe') { const draw = s.decks[side].shift(); if (draw) s.hands[side].push(draw); }
    if (c.effect === 'muster') for (const u of s.board[side]) if (u !== s.board[side].at(-1) && u.lane === move.lane) u.bonus++;
    s.last = id;
  }
  if (move.kind === 'recall' || move.kind === 'shift') {
    const u = s.board[side][move.index]; played = u.id;
    if (move.kind === 'recall') { s.board[side].splice(move.index, 1); s.hands[side].push(u.id); }
    else u.lane = u.lane === 0 ? 1 : 0;
    s.orders![side]--; s.last = move.kind;
  }
  if (record) {
  s.beat = { side, kind: move.kind, ...(played ? { card: played } : {}), ...('lane' in move ? { lane: move.lane } : {}), before, after: [score(s, 0), score(s, 1)], serial: (state.beat?.serial ?? 0) + 1 };
  if (s.ruleset === 2) {
    const changes: NonNullable<Duel['beat']>['changes'] = [];
    for (const p of [0, 1] as const) for (const u of s.board[p]) {
      const previous = state.board[p].find(old => old.id === u.id);
      if (!previous) continue;
      const a = unitPower(previous, state.board[p]), b = unitPower(u, s.board[p]);
      if (a !== b) changes.push({ side: p, id: u.id, before: a, after: b });
    }
    if (changes.length) s.beat.changes = changes;
    const owner = (d: Duel): 0 | 1 | null => standardBonus(d, 0) ? 0 : standardBonus(d, 1) ? 1 : null;
    const a = owner(state), b = owner(s);
    if (a !== b) s.beat.standard = { before: a, after: b };
  }
  }
  for (const p of [0, 1] as const) if (s.hands[p].length === 0 && (s.ruleset === 2 ? !s.orders?.[p] || !s.board[p].length : s.leader[p] || s.board[p].length === 0)) s.passed[p] = true;
  if (s.passed[0] && s.passed[1]) finishRound(s);
  else s.turn = s.passed[enemy] ? side : enemy;
  return s;
}
export function nextRound(state: Duel): Duel {
  if (state.status !== 'round') return state;
  const s = structuredClone(state);
  s.round++; s.status = 'playing'; s.passed = [false, false]; s.turn = s.round % 2 === 0 ? 1 : 0;
  s.board = [[], []];
  for (const side of [0, 1] as const) s.hands[side].push(...s.decks[side].splice(0, 2));
  s.last = 'newRound';
  return s;
}
/** A narrow proof, not a peek at the rival's hidden cards. With no draws/orders
 * and at most two unknown cards, exhaust every legal identity and placement.
 * If any line can tie or overtake us, keep playing. Larger hands are uncertain. */
export function hasUnassailableLead(s: Duel): boolean {
  const side = s.turn, enemy = side === 0 ? 1 : 0, count = s.hands[enemy].length;
  if (s.ruleset !== 2 || score(s, side) <= score(s, enemy) || count < 1 || count > 2 || s.decks[enemy].length || s.orders?.[enemy]) return false;
  const available = CARD_POOL.filter(c => !s.board[enemy].some(u => u.id === c.id));
  const safe = (state: Duel, pool: readonly CardDef[], left: number): boolean => {
    for (const card of pool) for (const lane of [0, 1] as const) {
      if (card.lane !== 'any' && card.lane !== lane) continue;
      const publicState = structuredClone(state);
      publicState.status = 'playing'; publicState.passed = [false, false]; publicState.turn = enemy;
      publicState.hands[enemy] = [card.id];
      const after = applyMove(publicState, { kind: 'play', index: 0, lane });
      if (score(after, side) <= score(after, enemy)) return false;
      if (left > 1 && !safe(after, pool.filter(c => c.id !== card.id), left - 1)) return false;
    }
    return true;
  };
  return safe(s, available, count);
}
export function chooseAiMove(s: Duel): Move {
  const side = s.turn, enemy = side === 0 ? 1 : 0;
  const own = score(s, side), other = score(s, enemy);
  if (s.aiVersion === 2 && s.ruleset === 2 && s.passed[enemy]) {
    const conceded = applyMove(s, { kind: 'pass' });
    if (conceded.status === 'finished' && conceded.wins[side] > conceded.wins[enemy]) return { kind: 'pass' };
  }
  if (s.passed[enemy] && own > other) {
    // A won, non-final round can pay for the next one: rescue a strong unit,
    // but only if the complete visible score (including the standard) stays ahead.
    if (s.ruleset === 2 && s.wins[side] === 0 && s.round < 3) {
      const rescue = legalMoves(s).filter((m): m is Extract<Move, { kind: 'recall' | 'shift' }> => m.kind === 'recall')
        .filter(m => cardById(s.board[side][m.index].id).power >= 3 && score(applyMove(s, m), side) > score(applyMove(s, m), enemy))
        .sort((a, b) => cardById(s.board[side][b.index].id).power - cardById(s.board[side][a.index].id).power)[0];
      if (rescue) return rescue;
    }
    return { kind: 'pass' };
  }
  if (hasUnassailableLead(s)) return { kind: 'pass' };
  const moves = legalMoves(s).filter(m => m.kind !== 'pass');
  if (!moves.length) return { kind: 'pass' };
  if (!s.passed[enemy] && other - own >= 12 && s.wins[enemy] === 0 && s.hands[side].length <= s.hands[enemy].length) return { kind: 'pass' };
  const scored = moves.map(m => ({ m, utility: immediateUtility(s, m) }));
  scored.sort((a, b) => b.utility - a.utility);
  // Saved opponents keep their existing policy until a newly dealt match.
  if (s.ruleset !== 2 || s.aiVersion !== 2 || s.passed[enemy]) return scored[0].m;
  return boundedPlan(s, scored).move;
}
function immediateUtility(s: Duel, m: Move): number {
  const side = s.turn, enemy = side === 0 ? 1 : 0, own = score(s, side), other = score(s, enemy);
    const after = transition(s, m, false);
    const gain = score(after, side) - own + other - score(after, enemy);
    let utility = gain;
    if (m.kind === 'play') {
      const c = cardById(s.hands[side][m.index]);
      if (c.effect === 'scribe') utility += s.decks[side].length ? 4 : 0;
      if (c.effect === 'banner' && s.hands[side].length > 3) utility += 2;
      if (c.effect === 'bond') utility += s.hands[side].filter(id => id !== UNKNOWN && cardById(id).family === c.family).length;
      // Style only values our own hand and the open board. No enemy card IDs
      // or future deck order may inform a choice; old saved duels keep old AI.
      const style = side === 1 ? s.opponent?.style : undefined;
      if (style === 'company' && c.effect === 'bond') utility += s.hands[side].filter(id => id !== UNKNOWN && id !== c.id && cardById(id).family === c.family).length * .7;
      if (style === 'raiders' && c.effect === 'raid') utility += s.board[enemy].some(u => u.lane === m.lane) ? 1.5 : -2;
      if (style === 'archive' && c.effect === 'scribe' && s.decks[side].length) utility += 1.5;
      if (style === 'procession' && c.effect === 'banner' && s.hands[side].length > 3) utility += 1.5;
      if (style === 'procession' && c.effect === 'muster' && s.board[side].filter(u => u.lane === m.lane).length < 2) utility -= 2;
    }
    if (s.passed[enemy]) utility = score(after, side) > other ? 100 - gain : gain;
    if (m.kind === 'leader' && !s.passed[enemy] && gain < 6) utility -= 4;
    // Spend limited orders only when they improve the visible position or
    // rescue a valuable card from a round already deliberately conceded.
    if (m.kind === 'shift') utility -= 2;
    if (m.kind === 'recall') utility = s.passed[enemy] && own - unitPower(s.board[side][m.index], s.board[side]) > other + 4 ? 12 : -20;
  return utility;
}

const UNKNOWN = '__unseen_card__';
/** Deliberately erase hidden identities and both draw orders before searching.
 * Unknown cards preserve public counts/automatic passing, but can never be played
 * by our continuation search. A scribe's draw is valued as a card, not a prophecy. */
function publicProjection(s: Duel): Duel {
  const side = s.turn, enemy = side === 0 ? 1 : 0;
  return { ...s, hands: side === 0 ? [[...s.hands[0]], Array(s.hands[enemy].length).fill(UNKNOWN)] : [Array(s.hands[enemy].length).fill(UNKNOWN), [...s.hands[1]]],
    decks: [Array(s.decks[0].length).fill(UNKNOWN), Array(s.decks[1].length).fill(UNKNOWN)],
    board: s.board.map(row => row.map(u => ({ ...u }))) as Duel['board'], beat: undefined };
}
export function evaluateCardPosition(s: Duel, side: 0 | 1): number {
  const enemy = side === 0 ? 1 : 0, margin = score(s, side) - score(s, enemy);
  const hand = (who: 0 | 1): number => s.hands[who].reduce((n, id) => n + (id === UNKNOWN ? 4 : 2.8 + cardById(id).power * .32), 0);
  // Once ahead, extra points matter less than retaining a second-round hand.
  const board = Math.tanh(margin / 16) * 16;
  const resources = hand(side) - hand(enemy) + ((s.orders?.[side] ?? 0) - (s.orders?.[enemy] ?? 0)) * 2.2;
  const terminal = s.status === 'finished' ? Math.sign(s.wins[side] - s.wins[enemy]) * 1000 : s.status === 'round' ? Math.sign(margin) * 24 : 0;
  return board + resources + terminal;
}
interface PlanningChoice { m: Move; utility: number }
export interface CardPlan { move: Move; candidates: number; replies: number; continuations: number }
/** Six first moves, three worst public replies, one own continuation.
 * Opposing cards are hypothetical catalog identities, never their saved hand.
 * Order replies (including a shifted standard) are generated from the open board. */
function boundedPlan(s: Duel, ranked: PlanningChoice[]): CardPlan {
  const side = s.turn, enemy = side === 0 ? 1 : 0, start = publicProjection(s);
  const stats: CardPlan = { move: ranked[0].m, candidates: 0, replies: 0, continuations: 0 };
  let best = -Infinity;
  for (const candidate of ranked.slice(0, 6)) {
    stats.candidates++;
    const after = transition(start, candidate.m, false);
    const replies: Duel[] = [];
    if (after.status === 'playing' && after.turn === enemy) {
      for (const move of legalMoves(after).filter(m => m.kind !== 'play')) replies.push(transition(after, move, false));
      if (after.hands[enemy].length) for (const card of CARD_POOL) {
        if (after.board[enemy].some(u => u.id === card.id)) continue;
        const view = { ...after, hands: after.hands.map(h => [...h]) as Duel['hands'] };
        view.hands[enemy][0] = card.id;
        for (const lane of [0, 1] as const) if (card.lane === 'any' || card.lane === lane) replies.push(transition(view, { kind: 'play', index: 0, lane }, false));
      }
    }
    stats.replies += replies.length;
    const threats = replies.map(state => ({ state, value: evaluateCardPosition(state, side) })).sort((a, b) => a.value - b.value).slice(0, 3).map(x => x.state);
    if (!threats.length) threats.push(after);
    let worst = Infinity;
    for (const reply of threats) {
      let continuation = evaluateCardPosition(reply, side);
      if (reply.status === 'playing' && reply.turn === side && !reply.passed[side]) {
        const options = legalMoves(reply).filter(m => m.kind !== 'play' || reply.hands[side][m.index] !== UNKNOWN)
          .filter(m => m.kind !== 'pass' || reply.passed[enemy])
          .map(m => ({ m, utility: immediateUtility(reply, m) })).sort((a, b) => b.utility - a.utility).slice(0, 8).map(x => x.m);
        if (options.length) {
          continuation = -Infinity;
          for (const move of options) {
            stats.continuations++;
            continuation = Math.max(continuation, evaluateCardPosition(transition(reply, move, false), side));
          }
        }
      }
      worst = Math.min(worst, continuation);
    }
    // Keep the public style's habits as a tie-breaker, not a substitute for foresight.
    const value = worst * .7 + evaluateCardPosition(after, side) * .3 + candidate.utility * .22;
    if (value > best + 1e-8) { best = value; stats.move = candidate.m; }
  }
  return stats;
}
/** Read-only diagnostics for finite planning-budget tests and the playtest harness. */
export function inspectCardPlan(s: Duel): CardPlan {
  const ranked = legalMoves(s).filter(m => m.kind !== 'pass').map(m => ({ m, utility: immediateUtility(s, m) })).sort((a, b) => b.utility - a.utility);
  return ranked.length ? boundedPlan(s, ranked) : { move: { kind: 'pass' }, candidates: 0, replies: 0, continuations: 0 };
}

export function runOpponent(state: Duel): Duel {
  let s = state;
  for (let n = 0; n < 32 && s.status === 'playing' && s.turn === 1; n++) s = applyMove(s, chooseAiMove(s));
  return s;
}
